import { useCallback, useEffect, useRef, useState } from "react";

interface UseDrawerResizeOptions {
    initialWidth: number;
    minWidth: number;
    maxWidthRatio?: number;
    /** When set, the last dragged-to width is persisted here and restored on mount. */
    storageKey?: string;
}

function clampWidth(width: number, minWidth: number, maxWidthRatio: number): number {
    const maxDesktopDrawerWidth = Math.floor(window.innerWidth * maxWidthRatio);
    return Math.min(maxDesktopDrawerWidth, Math.max(minWidth, width));
}

function readStoredWidth(storageKey: string | undefined): number | null {
    if (!storageKey) {
        return null;
    }
    const stored = window.localStorage.getItem(storageKey);
    const parsed = stored ? Number.parseInt(stored, 10) : NaN;
    return Number.isFinite(parsed) ? parsed : null;
}

export function useDrawerResize({
    initialWidth,
    minWidth,
    maxWidthRatio = 0.6,
    storageKey,
}: UseDrawerResizeOptions) {
    const [drawerWidth, setDrawerWidth] = useState<number>(() => {
        const stored = readStoredWidth(storageKey);
        return stored === null ? initialWidth : clampWidth(stored, minWidth, maxWidthRatio);
    });
    const [isResizing, setIsResizing] = useState<boolean>(false);
    // mousemove only reaches `setDrawerWidth` (state, async), but persisting on mouseup
    // needs the value from the very last move, so it's tracked in a ref alongside it.
    const latestWidthRef = useRef(drawerWidth);

    const startResize = useCallback(() => {
        setIsResizing(true);
    }, []);

    useEffect(() => {
        if (!isResizing) {
            return;
        }

        let frame: number | null = null;
        let pendingX = 0;

        const applyWidth = (clientX: number) => {
            const nextWidth = clampWidth(clientX, minWidth, maxWidthRatio);
            latestWidthRef.current = nextWidth;
            setDrawerWidth(nextWidth);
        };

        const handleMouseMove = (e: MouseEvent) => {
            pendingX = e.clientX;
            if (frame !== null) {
                return;
            }

            frame = window.requestAnimationFrame(() => {
                frame = null;
                applyWidth(pendingX);
            });
        };

        const handleMouseUp = () => {
            setIsResizing(false);
            if (storageKey) {
                window.localStorage.setItem(storageKey, String(latestWidthRef.current));
            }
        };

        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);

        return () => {
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
            if (frame !== null) {
                window.cancelAnimationFrame(frame);
            }
        };
    }, [isResizing, maxWidthRatio, minWidth, setDrawerWidth, setIsResizing, storageKey]);

    return {
        drawerWidth,
        startResize,
    };
}
